/**
 * Simple in-memory cache manager for the application
 * Provides methods to store, retrieve, and invalidate cached data
 */

interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

class CacheManager {
  private cache: Map<string, CacheItem<any>> = new Map();
  private defaultTTL: number = 5 * 60 * 1000; // 5 minutes in milliseconds

  /**
   * Set a value in the cache
   * @param key - The cache key
   * @param value - The value to cache
   * @param ttl - Time to live in milliseconds (optional, defaults to 5 minutes)
   */
  set<T>(key: string, value: T, ttl: number = this.defaultTTL): void {
    const now = Date.now();
    this.cache.set(key, {
      data: value,
      timestamp: now,
      expiresAt: now + ttl
    });
  }

  /**
   * Get a value from the cache
   * @param key - The cache key
   * @returns The cached value or null if not found or expired
   */
  get<T>(key: string): T | null {
    const item = this.cache.get(key);
    
    if (!item) {
      return null;
    }
    
    // Check if the item has expired
    if (Date.now() > item.expiresAt) {
      this.delete(key);
      return null;
    }
    
    return item.data as T;
  }

  /**
   * Delete a value from the cache
   * @param key - The cache key
   */
  delete(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Delete all cache entries with a given prefix
   * @param prefix - The prefix to match
   * @returns The number of items removed
   */
  deleteByPrefix(prefix: string): number {
    let count = 0;
    
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key);
        count++;
      }
    }
    
    return count;
  }

  /**
   * Check if a key exists in the cache and is not expired
   * @param key - The cache key
   * @returns True if the key exists and is not expired
   */
  has(key: string): boolean {
    const item = this.cache.get(key);
    
    if (!item) {
      return false;
    }
    
    // Check if the item has expired
    if (Date.now() > item.expiresAt) {
      this.delete(key);
      return false;
    }
    
    return true;
  }

  /**
   * Clear all items from the cache
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Clear expired items from the cache
   * @returns The number of items removed
   */
  clearExpired(): number {
    const now = Date.now();
    let count = 0;
    
    for (const [key, item] of this.cache.entries()) {
      if (now > item.expiresAt) {
        this.cache.delete(key);
        count++;
      }
    }
    
    return count;
  }

  /**
   * Get the number of items in the cache
   */
  get size(): number {
    return this.cache.size;
  }

  /**
   * Get or set a value in the cache with a callback function
   * If the key exists and is not expired, returns the cached value
   * Otherwise, calls the callback function, caches the result, and returns it
   * 
   * @param key - The cache key
   * @param callback - Function to call if the key is not in the cache
   * @param ttl - Time to live in milliseconds (optional)
   * @returns The cached or newly fetched value
   */
  async getOrSet<T>(
    key: string, 
    callback: () => Promise<T>, 
    ttl: number = this.defaultTTL
  ): Promise<T> {
    const cachedValue = this.get<T>(key);
    
    if (cachedValue !== null) {
      return cachedValue;
    }
    
    const value = await callback();
    this.set(key, value, ttl);
    return value;
  }
}

// Export a singleton instance
export const cacheManager = new CacheManager(); 